import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Avatar,
  IconButton,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import HomeNavbar from '../components/home-nav-bar';
import ToastMessage from '../components/toast-message';
import SuccessDialog from '../components/success-dialog';
import { Delete, Edit, UploadFile } from '@mui/icons-material';
import uploadFileToCloud from '../utils/upload-file';
import { useNavigate } from 'react-router-dom';

function EditorialBoardApplication() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [paperTypes, setPaperTypes] = useState({
    reviewing_manuscripts: false,
    editorial_policices_advice: false,
    journal_strategy_development_help: false
  });
  const [formDetails, setFormDetails] = useState({
    personalInfo: {
      name: '',
      title: '',
      affiliation: '',
      department: '',
      profile_image: null,
      email: '',
      phone: ''
    },
    academicBackground: {
      highest_degree: '',
      areas_of_expertise: '',
      years_of_experience: '',
    },
    publicationAndEditorialExperience: {
      number_of_papers_published: '',
      cv: null
    },
    availability_for: []
  });

  
  const [loading, setLoading] = useState(false);

  const steps = [
    'Personal Information',
    'Academic Background',
    'Publication and Editorial Experience',
    'Availability and Commitment'
  ];

  const [status, setStatus] = useState({
      type: 0,
      message: ''
  });

  const [actionSuccessful, setActionSuccessful] = useState(false);

  const clearToastMessge = () => {
      setStatus({
          type: 0,
          message: ''
      });
  };
  

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      // confirm required fields are filled in

      for (const key of Object.keys(formDetails.personalInfo)) {
        if (!formDetails.personalInfo[key]) {
          setStatus({
            type: 0,
            message: `${key} from section 1 is required`
          });
          return;
        }
      }

      for (const key of Object.keys(formDetails.academicBackground)) {
        if (!formDetails.academicBackground[key]) {
          setStatus({
            type: 0,
            message: `${key} from section 2 is required`
          });
          return;
        }
      }

      if (!formDetails.publicationAndEditorialExperience.cv) {
          setStatus({
            type: 0,
            message: `upload your cv`
          });
          return;
      }

      setLoading(true);
      

      const file = formDetails.personalInfo.profile_image;
      const email = formDetails.personalInfo.email.trim();

      const extension = file.name.split('.').pop(); 

      const fileName = `${email}.${extension}`;

      const path = `wajls/editorial_board/profile_images/${fileName}`;

      
      const downloadURL = await uploadFileToCloud(file, path, (progress) => {
        console.log(`Upload Progress: ${progress.toFixed(2)}%`);
      });

      const cvPath = `wajls/editorial_board/cvs/${formDetails.publicationAndEditorialExperience.cv.name}`;
      const cvDownloadUrl = await uploadFileToCloud(formDetails.publicationAndEditorialExperience.cv, cvPath, (progress) => {
        console.log(`Upload Progress: ${progress.toFixed(2)}%`);
      });

      const updatedFormDetails = {
        ...formDetails,
        personalInfo: {
          ...formDetails.personalInfo,
          profile_image: downloadURL
        }, 
        publicationAndEditorialExperience: {
          ...formDetails.publicationAndEditorialExperience,
          cv: cvDownloadUrl
        },
        availability_for: Object.keys(paperTypes)
          .filter(key => paperTypes[key])
          .join(', ') 
      };

      setFormDetails(updatedFormDetails);

      
      try {
        const response = await axios.post('https://api.wajls.com/client/v1/join_editorial_board.php', updatedFormDetails, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        
        setLoading(false);
        
        if (response.data.success) {
          setActionSuccessful(true);
        }
        else {
          setStatus({
            type: 0,
            message: response.data.message
          });
        }
  
        
      } catch (error) {
        console.error(error);
        setLoading(false);
      } 
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handlePersonalInfoChange("profile_image", file);
    }
  };

  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handlePubAndEditChange("cv", file);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setFormDetails({
      ...formDetails,
      personalInfo: {
        ...formDetails.personalInfo,
        [field]: value
      }
    });
  };

  const handleAcademicBackgroundChange = (field, value) => {
    setFormDetails({
      ...formDetails,
      academicBackground: {
        ...formDetails.academicBackground,
        [field]: value
      }
    });
  };

  const handlePubAndEditChange = (field, value) => {
    setFormDetails({
      ...formDetails,
      publicationAndEditorialExperience: {
        ...formDetails.publicationAndEditorialExperience,
        [field]: value
      }
    });
  };

  const handlePaperTypeChange = (event) => {
    setPaperTypes({
      ...paperTypes,
      [event.target.name]: event.target.checked
    });
  };


  const renderPersonalInfo = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>

          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block',
            }}
            
          >
            Personal Information
          </Typography>

        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar 
            src={
              formDetails.personalInfo.profile_image && URL.createObjectURL(formDetails.personalInfo.profile_image)
            } 
            sx={{ width: 80, height: 80, border: '1px solid #000000' }} />
            
          <IconButton title='upload profile image' color="primary" component="label">
              <Edit />
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </IconButton>
          <IconButton color="error" title='delete profile image' onClick={() => {
              handlePersonalInfoChange("profile_image", null);
          }}>
              <Delete />
          </IconButton>
        </Box>

        <TextField
          required
          fullWidth
          label="Full Name"
          variant="outlined"
          value={formDetails.personalInfo.name}
          onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
          sx={{ mb: 3, backgroundColor: '#fff' }}
        />
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Title/Position"
              variant="outlined"
              value={formDetails.personalInfo.title}
              onChange={(e) => handlePersonalInfoChange('title', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Affiliation/Organization"
              variant="outlined"
              value={formDetails.personalInfo.affiliation}
              onChange={(e) => handlePersonalInfoChange('affiliation', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
        </Grid>
        <TextField
          required
          fullWidth
          label="Department/Faculty"
          variant="outlined"
          value={formDetails.personalInfo.department}
          onChange={(e) => handlePersonalInfoChange('department', e.target.value)}
          sx={{ mt: 3, mb: 3, backgroundColor: '#fff' }}
        />
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              value={formDetails.personalInfo.email}
              onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              label="Phone"
              variant="outlined"
              value={formDetails.personalInfo.phone}
              onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render the Academic Background section
  const renderAcademicBackground = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            Academic Background
          </Typography>


     <FormControl fullWidth required sx={{ mb: 3, backgroundColor: '#fff' }}>
      <InputLabel>Highest Degree</InputLabel>
      <Select
        value={formDetails.academicBackground.highest_degree || ""}
        label="Highest Degree"
        onChange={(e) => handleAcademicBackgroundChange("highest_degree", e.target.value)}
      >
        <MenuItem value="BSc">BSc</MenuItem>
        <MenuItem value="MSc">MSc</MenuItem>
        <MenuItem value="PhD">PhD</MenuItem>
        <MenuItem value="Others">Others</MenuItem>
      </Select>
    </FormControl>


        <TextField
          fullWidth
          variant="outlined"
          placeholder="Please List your areas of expertise, separated by commas"
          multiline
          rows={5}
          value={formDetails.academicBackground.areas_of_expertise}
          onChange={(e) => handleAcademicBackgroundChange('areas_of_expertise', e.target.value)}
          sx={{ mb: 3, backgroundColor: '#fff' }}
        />


        <TextField
          required
          fullWidth
          label="Total years of experience"
          variant="outlined"
          value={formDetails.academicBackground.years_of_experience}
          onChange={(e) => handleAcademicBackgroundChange('years_of_experience', e.target.value)}
          sx={{ mb: 3, backgroundColor: '#fff' }}
        />
      </Box>
    );
  };

  // Render the experience section
  const renderPubAndEdit = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            Publication and Editorial Experience
          </Typography>
          
          <TextField
            required
            label="Number of published papers"
            type="number"
            variant="outlined"
            fullWidth
            value={formDetails.publicationAndEditorialExperience.number_of_papers_published}
            onChange={(e) => handlePubAndEditChange('number_of_papers_published', e.target.value)}
            sx={{ mb: 3, backgroundColor: '#fff' }}
          />

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            sx={{ 
              textTransform: 'none', 
              justifyContent: 'flex-start', 
              backgroundColor: 'white',
              alignSelf: 'flex-start' 
            }}
          >
            {formDetails.publicationAndEditorialExperience.cv ? formDetails.publicationAndEditorialExperience.cv.name : 'Upload CV (PDF or DOCX)'}
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              hidden
              onChange={handleCvUpload}
            />
          </Button>

        </Box>

      </Box>
    );
  };

  // Render the availability section
  const renderAvailabilityAndCommitment = () => {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              mb: 3, 
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              paddingBottom: 1,
              display: 'inline-block'
            }}
          >
            Availability and Commitment
          </Typography>
          
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel 
              component="legend" 
              sx={{ 
                mb: 2, 
                fontWeight: 500,
                color: theme.palette.text.primary 
              }}
            >
              Available for:
            </FormLabel>

            <FormGroup>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paperTypes.reviewing_manuscripts}
                        onChange={handlePaperTypeChange}
                        name="reviewing_manuscripts"
                        color="primary"
                      />
                    }
                    label="Reviewing manuscripts"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paperTypes.editorial_policices_advice}
                        onChange={handlePaperTypeChange}
                        name="editorial_policices_advice"
                        color="primary"
                      />
                    }
                    label="Advising on editorial policies"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paperTypes.journal_strategy_development_help}
                        onChange={handlePaperTypeChange}
                        name="journal_strategy_development_help"
                        color="primary"
                      />
                    }
                    label="Helping with journal strategy development"
                  />
                </Grid>
              
              </Grid>
              
            </FormGroup>
          </FormControl>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ backgroundColor: "grey.100", minHeight: '100vh' }}>
      <HomeNavbar/>
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 5 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Editorial Board Membership Application to West African Journal of Life Sciences
            </Typography>
          </Box>

          {/* Horizontal stepper for both mobile and desktop */}
          <Box sx={{ width: '100%', mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel sx={{ fontWeight: 'bold' }}></StepLabel>
                </Step>
              ))}
            </Stepper>
          
          </Box>

          {activeStep === 0 && renderPersonalInfo()}
          {activeStep === 1 && renderAcademicBackground()}
          {activeStep === 2 && renderPubAndEdit()}
          {activeStep === 3 && renderAvailabilityAndCommitment()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              color="primary"
              endIcon={activeStep === steps.length - 1 ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
              sx={{ 
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {activeStep === steps.length - 1 ? 'Submit Application' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Container>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>

      {status.message && <ToastMessage type={status.type} message={status.message} onClose={clearToastMessge}/>}
      <SuccessDialog open={actionSuccessful} article={false} email={formDetails.personalInfo.email} onClose={()=> {
        setActionSuccessful(false);
        navigate("/");
      }}/>
    </Box>
  );
}

export default EditorialBoardApplication;